import type { LiveAccount, LiveSupport, ParamValue, StartLiveBody } from './types'

/**
 * Spec 041: the Paper account block's pure side: the form model, validation
 * (the server validates too, as `invalid_account`), the start body and the
 * Start live control state.
 */
export interface AccountForm {
  starting_balance: string
  commission_per_contract: string
  max_contracts: string
}

export type AccountField = keyof AccountForm

export function accountFormFrom(account: LiveAccount): AccountForm {
  return {
    starting_balance: String(account.starting_balance),
    commission_per_contract: String(account.commission_per_contract),
    max_contracts: String(account.max_contracts),
  }
}

export function isAccountChanged(field: AccountField, form: AccountForm, defaults: LiveAccount): boolean {
  const typed = form[field].trim()
  if (typed === '') return true
  const n = Number(typed)
  return !Number.isFinite(n) || n !== defaults[field]
}

function parse(text: string): number | null {
  const t = text.trim()
  if (t === '') return null
  const n = Number(t)
  return Number.isFinite(n) ? n : null
}

export function validateAccount(form: AccountForm): {
  values: LiveAccount | null
  errors: Partial<Record<AccountField, string>>
} {
  const errors: Partial<Record<AccountField, string>> = {}
  const balance = parse(form.starting_balance)
  const commission = parse(form.commission_per_contract)
  const max = parse(form.max_contracts)
  if (balance === null || balance <= 0) errors.starting_balance = 'Enter a number above 0.'
  if (commission === null || commission < 0) errors.commission_per_contract = 'Enter a number of 0 or more.'
  if (max === null || !Number.isInteger(max) || max < 1 || max > 1000) errors.max_contracts = 'Enter a whole number from 1 to 1000.'
  if (Object.keys(errors).length > 0 || balance === null || commission === null || max === null) {
    return { values: null, errors }
  }
  return { values: { starting_balance: balance, commission_per_contract: commission, max_contracts: max }, errors }
}

/**
 * The form's coerced params, then `support.defaults` over them (a defaults key
 * replaces the form value), then every `ignored_params` name removed (ignored wins).
 */
export function buildLiveStartBody(args: {
  strategy: string
  values: Record<string, ParamValue>
  support: LiveSupport
  account: LiveAccount
}): StartLiveBody {
  const params: Record<string, ParamValue> = { ...args.values, ...args.support.defaults }
  for (const name of args.support.ignored_params) delete params[name]
  return { strategy: args.strategy, params, account: args.account }
}

/** The reason shown (and the server 422 `instrument_not_live` copy) when the instrument has no live feed. */
export const NO_LIVE_FEED = 'No live feed for this instrument.'

export function startLiveControl(args: {
  support: LiveSupport | null | undefined
  liveError: string | null | undefined
  cap: { active: number; max: number; ownActive: number } | null
  starting: boolean
  /** Spec 070: the instrument the session would trade and the feed's `live_instruments` (`null`/absent = the feed did not say). */
  instrument?: string | null
  liveInstruments?: readonly string[] | null
}): { enabled: boolean; label: string; status: string } {
  const label = '▶ Start live (paper)'
  const off = (status: string, l = label): { enabled: boolean; label: string; status: string } => ({
    enabled: false,
    label: l,
    status,
  })
  if (args.starting) return off('Starting the session. This takes about 6 seconds.', 'Starting… (about 6 s)')
  if (typeof args.liveError === 'string' && args.liveError !== '') {
    return off(`Live support is broken for this strategy: ${args.liveError}`)
  }
  if (args.support === null || args.support === undefined) return off('This strategy does not support live sessions.')
  if (
    Array.isArray(args.liveInstruments) &&
    typeof args.instrument === 'string' &&
    !args.liveInstruments.includes(args.instrument)
  ) {
    return off(NO_LIVE_FEED)
  }
  if (args.cap !== null && args.cap.active >= args.cap.max) {
    return off(
      args.cap.ownActive === 0
        ? 'Held by another user. Starts are refused.'
        : `${args.cap.active} of ${args.cap.max} live sessions are running. Stop one first.`,
    )
  }
  return { enabled: true, label, status: 'Starts a paper session. Takes about 6 seconds.' }
}
